/**
 * `startSourcePinReconciler` — the AUTHORITATIVE all-tabs `sourcePin`
 * severing path (G5).
 *
 * `useWorkspacePinState` (Task 18) already severs a single MOUNTED tab's
 * `sourcePin` live, per-tab, whenever its pin disappears from a hydrated
 * `byConnection` slot. This module covers what that per-tab hook can't:
 *
 *  - tabs whose pin button isn't currently mounted/rendered (background tabs)
 *  - the post-session-restore case: a restored tab may reference a pin that
 *    was deleted while the app was closed. Session restore reconnects, which
 *    triggers `usePinnedObjectsStore.hydrate(connectionId)`, which writes a
 *    fresh (or first) `byConnection[cid]` slot — this subscriber fires on
 *    that write and reconciles every tab for that connection at once.
 *
 * Both paths are idempotent (clearing an already-clear `sourcePin` is a
 * no-op), so the overlap between the live per-tab hook and this all-tabs
 * reconciler is intentional redundancy, not a conflict.
 *
 * Subscribe signature — zustand v5's vanilla `create()` store exposes
 * `subscribe((state, prevState) => void)` by default (no `subscribeWithSelector`
 * middleware needed for the whole-state form). `pinnedObjectsStore` is a plain
 * `create<State>(...)` store, and `DatabaseExplorer.tsx` already relies on this
 * exact two-arg signature for its own pinned-children rebuild subscription, so
 * this module follows the same, already-proven contract.
 */

import { usePinnedObjectsStore } from '../../store/pinnedObjectsStore';
import { useWorkspaceStore } from '../../../workspace/core/store/workspaceStore';
import { isWorkspacePinRef } from './pinRef';
import type { PinnedWorkspaceRef } from '../../../../types/electron';

/**
 * Starts the reconciler. Returns an unsubscribe function — callers must
 * invoke it on unmount so the app doesn't accumulate duplicate subscriptions
 * across remounts (e.g. React StrictMode double-invoke, or hot reload).
 */
export function startSourcePinReconciler(): () => void {
  return usePinnedObjectsStore.subscribe((state, prev) => {
    for (const cid of Object.keys(state.byConnection)) {
      // Unchanged slot (same object reference) — nothing to reconcile. Also
      // means a NEW key relative to `prev` always reconciles (prev.byConnection[cid]
      // is `undefined`, which never === the new slot).
      if (state.byConnection[cid] === prev.byConnection[cid]) continue;

      const ids = new Set(
        state.byConnection[cid].pinnedObjects
          .filter(isWorkspacePinRef)
          .map((p: PinnedWorkspaceRef) => p.id),
      );
      useWorkspaceStore
        .getState()
        .clearSourcePins((sp) => sp.connectionId === cid && !ids.has(sp.pinId));
    }
    // Slot DELETION (reorder-recovery rollback, `forget()` on disconnect) is
    // intentionally NOT iterated here: `Object.keys(state.byConnection)` only
    // visits keys present in the CURRENT snapshot, so a key that existed in
    // `prev` but is gone from `state` is simply skipped. Absence means
    // "unknown" (the connection isn't hydrated / was disconnected), never
    // "this connection has no pins" (G5) — clearing on absence would wrongly
    // sever every tab still holding a link to a connection we can no longer
    // confirm anything about.
  });
}
